import React, { useEffect, useMemo, useState } from "react";
import { cls } from "@wss/cls";
import { logException } from "@wss/error-tracking";
import StandardPrice from "./StandardPrice";
import PlusSignupLink from "../PlusSignupLink";
import useIsMounted from "~/common/useIsMounted";
import { ShippingAddress } from "~/ViewCart/Models";
import { User } from "~/common/Store/connectUserAndChat";
import GoogleViewPrice from "./GoogleViewPrice";
import PlusRegularPricing from "./PlusRegularPricing";
import PlusPageModal from "~/common/PlusSubscriptionModal/components/PlusPageModal";
import fetchPlusModalData from "./fetchPlusModalData";
import { EnrollmentOption, PlusModalData } from "~/common/PlusSubscriptionModal/Models";
import PlusSubscriptionModalStore, { ModalTypes } from "~/common/PlusSubscriptionModal/PlusSubscriptionModalStore";
import(/* webpackChunkName: "modals-standalone" */"~/bootstrap/less/modals-standalone.less");
interface Props {
  unitOfMeasure: string;
  plusMemberPrice: number;
  regularPrice: number;
  p1Price: number;
  showCrossoutPrice: boolean;
  divideByUnit?: string;
  divideQuantity?: number;
  showPlusSignup: boolean;
  user: User;
  isGoogleView?: boolean;
  minimumBuy?: number;
  mustBuy?: number;
}
interface ShippingAddressWithDefault extends ShippingAddress {
  isDefault?: boolean;
}
const PlusPrice = ({
  unitOfMeasure,
  regularPrice,
  plusMemberPrice,
  showCrossoutPrice,
  p1Price,
  divideByUnit,
  divideQuantity,
  showPlusSignup,
  user,
  isGoogleView = false,
  minimumBuy = 1,
  mustBuy = 1
}: Props) => {
  const divideByUnitCssClass = "text-xs block ";
  const showDivideBy = !showPlusSignup;
  const setPlusModalOpen = PlusSubscriptionModalStore.getState().setModalOpen;
  const plusModalOpen = PlusSubscriptionModalStore.useState(state => state.modalType === ModalTypes.PLUS);
  const [plusPromotions, setPlusPromotion] = useState<EnrollmentOption[] | undefined>();
  const [plusModalData, setPlusModalData] = useState<PlusModalData>();
  const [defaultShippingAddressId, setDefaultShippingAddressId] = useState(0);
  const mounted = useIsMounted();
  useEffect(() => {
    const setPlusSignupModalData = async () => {
      const result = await fetchPlusModalData();
      if (result.isError) {
        logException(result.error);
      } else {
        const plusModalData = result.value;
        setPlusModalData(plusModalData);
        setPlusPromotion(plusModalData?.enrollmentOptions?.filter(e => e.promotionName));
        const shippingAddressess = plusModalData?.validAddresses.length ? plusModalData?.validAddresses.map(address => ({
          ...address
        })) as ShippingAddressWithDefault[] : [];
        const defaultAddressId = shippingAddressess.find(address => address.isDefault)?.id || shippingAddressess[0]?.id || 0;
        setDefaultShippingAddressId(defaultAddressId);
      }
    };
    setPlusSignupModalData();
  }, [plusModalOpen, user]);
  const validAddressesLength = useMemo(() => plusModalData?.validAddresses.length ?? 0, [plusModalData]);
  useEffect(() => {
    setPlusModalOpen(ModalTypes.CLOSED);
    if (mounted && window.location.search.includes("showform=1") && plusModalData) {
      if (validAddressesLength > 0 || !user.registered) {
        setPlusModalOpen(ModalTypes.PLUS);
      } else if (validAddressesLength === 0 && user.registered) {
        setPlusModalOpen(ModalTypes.INELIGIBLE);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, validAddressesLength, user.registered]);
  return <>
      {showPlusSignup && plusModalData && mounted && <PlusPageModal mounted={mounted} plusModalData={plusModalData} defaultShippingAddressId={defaultShippingAddressId} enrollment={plusPromotions ? plusPromotions[0] : undefined} addressPlusSignup={false} plusPotentialSavingsInfo={undefined} isFreeview={false} />}
      <div className="plus-member plus-member-override plus-member--plus">
        <div className="plus-member__group xxxxl:flex-nowrap">
          <div className="plus-member__text plus-member__price" data-testid="regular-price-container">
            <PlusRegularPricing unitOfMeasure={unitOfMeasure} regularPrice={regularPrice} p1Price={p1Price} showCrossoutPrice={showCrossoutPrice} divideByUnit={divideByUnit} divideQuantity={divideQuantity} isGoogleView={isGoogleView} minimumBuy={minimumBuy} mustBuy={mustBuy} divideByUnitCssClass={divideByUnitCssClass} data-sentry-element="PlusRegularPricing" data-sentry-source-file="PlusPrice.tsx" />
          </div>
          <div className="plus-member__price plus-member__price--plus-member flex justify-center flex-col" data-testid="plus-price-container">
            <label className={cls("price__label", showCrossoutPrice && "py-1")}>
              <span className="plus-flag">Plus</span> Member Price{" "}
            </label>
            {isGoogleView ? <GoogleViewPrice price={plusMemberPrice} unitOfMeasure={unitOfMeasure} showCreditKey={false} divideByUnit={showDivideBy ? divideByUnit : ""} divideQuantity={showDivideBy ? divideQuantity : undefined} divideByUnitCssClass={divideByUnitCssClass} minimumBuy={minimumBuy} mustBuy={mustBuy} /> : <StandardPrice price={plusMemberPrice} unitOfMeasure={unitOfMeasure} showCreditKey={false} divideByUnit={showDivideBy ? divideByUnit : ""} divideQuantity={showDivideBy ? divideQuantity : undefined} divideByUnitCssClass={divideByUnitCssClass} />}
            {showPlusSignup && <PlusSignupLink plusModalData={plusModalData} user={user} />}
          </div>
        </div>
      </div>
    </>;
};
export default PlusPrice;